import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeamNoun } from '@/hooks/useTeamNoun';

/**
 * The root teamspace's display name — "Main Teamspace" (#2210 naming). The root has no Team
 * document, so any surface that lists it alongside real teamspaces has to label it by hand; this
 * is the one label to use, the same expression `useTeamNameResolver` applies to a row with no
 * teamId (#2210: the Groups page still said "Default").
 *
 * The key IS the English string, which is the point. It used to be `Default {{noun}}` mapped to
 * "Main {{noun}}" — the only key in the file whose text contradicted its key. Every environment
 * loads i18n from a CDN bundle, and i18next falls back to the KEY when a string is missing there,
 * so a bundle published before #2133 rendered "Default Teamspace" — the old product name, looking
 * entirely deliberate rather than broken.
 */
export function useRootTeamspaceLabel(): string {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  return t('Main {{noun}}', { noun });
}

/**
 * The backend name of the root teamspace (open-api `_getRootTeam`): a SENTINEL, never a display
 * string. Only `useTeamDisplayName` should compare against it (#2389).
 */
export const ROOT_TEAM_SENTINEL_NAME = 'Default';

/**
 * A team's backend `name` → what to show: the root label for the `'Default'` sentinel, otherwise
 * the trimmed name ('' when blank, so callers pick their own fallback with `||`). The one place the
 * sentinel is resolved, so a new surface never prints "Default" (#2389).
 */
export function useTeamDisplayName(): (name?: string | null) => string {
  const rootLabel = useRootTeamspaceLabel();
  return useCallback(
    (name?: string | null) => (name === ROOT_TEAM_SENTINEL_NAME ? rootLabel : (name?.trim() ?? '')),
    [rootLabel],
  );
}
